import React, { useState, useEffect, useCallback } from "react";

import { Route, Routes, useParams, Navigate } from "react-router-dom";

import CompellonApi from "apis/compellon";
import ExploreContainer from "components/Common/ExploreContainer";
import PageNotFound from "components/Common/PageNotFound";

import Predict from "./Actions/Predict";
import WhatIf from "./Actions/WhatIf";
import ModelGraph from "./Insights/ModelGraph";
import Quality from "./Insights/Quality";
import RelationshipGraph from "./Insights/RelationshipGraph";
import MenuBar from "./MenuBar";
import PrimaryHeader from "./PrimaryHeader";
import Summary from "./Summary";

import { getOutputTypes } from "../utils";

const Results = () => {
  const { analysisId, datasetName } = useParams();

  const [isJobDetailsLoading, setIsJobDetailsLoading] = useState(true);
  const [isOutputsLoading, setIsOutputsLoading] = useState(true);
  const [isNewWhatIfPaneOpen, setIsNewWhatIfPaneOpen] = useState(false);

  const [jobDetails, setJobDetails] = useState();
  const [outputs, setOutputs] = useState();

  const fetchJobDetails = useCallback(async () => {
    setIsJobDetailsLoading(true);
    try {
      const {
        data: { job },
      } = await CompellonApi.fetchJobInfo({ job_id: analysisId });
      setJobDetails(job);
    } catch (error) {
      logger.error(error);
    } finally {
      setIsJobDetailsLoading(false);
    }
  }, [analysisId]);

  const fetchResults = useCallback(async () => {
    setIsOutputsLoading(true);
    try {
      const {
        data: { outputs },
      } = await CompellonApi.fetchJobResult({ job_id: analysisId });
      setOutputs(getOutputTypes(outputs));
    } catch (error) {
      logger.error(error);
    } finally {
      setIsOutputsLoading(false);
    }
  }, [analysisId]);

  useEffect(() => {
    if (analysisId) {
      fetchJobDetails();
      fetchResults();
    }
  }, [analysisId, fetchJobDetails, fetchResults]);

  return (
    <div className="flex">
      <MenuBar />
      <ExploreContainer className="w-full p-0 bg-transparent">
        <div className="p-8 cs-ui-container">
          <PrimaryHeader
            id={analysisId}
            name={jobDetails?.name}
            isLoading={isJobDetailsLoading}
            datasetName={datasetName}
            setIsNewWhatIfPaneOpen={setIsNewWhatIfPaneOpen}
          />
          <Routes>
            <Route
              path="summary"
              element={
                <Summary
                  isJobDetailsLoading={isJobDetailsLoading}
                  isOutputsLoading={isOutputsLoading}
                  jobDetails={jobDetails}
                  outputs={outputs}
                  setOutputs={setOutputs}
                />
              }
            />
            <Route
              path="relationship-graph"
              element={
                <RelationshipGraph
                  isLoading={isJobDetailsLoading || isOutputsLoading}
                  jobDetails={jobDetails}
                  outputs={outputs}
                  setOutputs={setOutputs}
                />
              }
            />
            <Route
              path="model-graph"
              element={
                <ModelGraph
                  isLoading={isJobDetailsLoading || isOutputsLoading}
                  jobDetails={jobDetails}
                  outputs={outputs}
                  setOutputs={setOutputs}
                />
              }
            />
            <Route
              path="quality"
              element={
                <Quality
                  isLoading={isOutputsLoading}
                  outputs={outputs}
                  setOutputs={setOutputs}
                />
              }
            />
            <Route
              path="what-if"
              element={
                <WhatIf
                  isOutputsLoading={isOutputsLoading}
                  outputs={outputs}
                  setOutputs={setOutputs}
                  isNewWhatIfPaneOpen={isNewWhatIfPaneOpen}
                  setIsNewWhatIfPaneOpen={setIsNewWhatIfPaneOpen}
                />
              }
            />
            <Route
              path="predict"
              element={
                <Predict
                  isLoading={isJobDetailsLoading}
                  jobDetails={jobDetails}
                />
              }
            />
            <Route path="/" element={<Navigate to="summary" />} />
            <Route path="*" element={<PageNotFound />} />
          </Routes>
        </div>
      </ExploreContainer>
    </div>
  );
};

export default Results;
